/**
 * Checks if the provided value strictly matches E.164 format.
 * E.164: + followed by 2-15 digits where the first digit is 1-9 (no leading zero), max length 15.
 */
export function isE164(value: string): boolean {
  if (!value) return false;
  return /^\+[1-9]\d{1,14}$/.test(value);
}

/**
 * Collapses a repeated NANP country code so a number normalizes the same with or without the
 * leading 1.
 *
 * Ten digits is the entire national number, so a longer value beginning with extra 1s is the country
 * code supplied twice — a form that prefills "+1" plus a user who types the 1 themselves. `+1` and a
 * further 11 digits is undialable, and left alone it reaches the carrier as an invalid destination.
 * Country code 1 IS the NANP, so this can't disturb an international number: no other country code
 * begins with 1.
 */
function collapseNanpCountryCode(digits: string): string {
  let collapsed = digits;
  while (collapsed.length > 11 && collapsed.startsWith("11")) {
    collapsed = collapsed.slice(1);
  }
  return collapsed;
}

/**
 * Converts an arbitrary phone input into strict E.164 format when possible.
 * - Strips all non-digit characters
 * - Enforces E.164 limits (max 15 digits, first digit 1-9)
 * - Supports a default country code (US "1" by default) for 10-digit local numbers
 * - Accepts a US number with or without its leading country-code 1, however many times it appears
 *
 * Examples:
 *  - "+1 (415) 555-2671" -> "+14155552671"
 *  - "(415) 555-2671" -> "+14155552671" (with defaultCountryCode="1")
 *  - "+1 1 415 555 2671" -> "+14155552671"
 *  - "442071838750" -> "+442071838750"
 */
export function toE164PhoneNumber(
  phoneNumber: string,
  defaultCountryCode: string = "1",
): string | undefined {
  if (!phoneNumber) return undefined;

  const trimmed = phoneNumber.trim();

  // If already includes '+', normalize by stripping non-digits and re-validating
  if (trimmed.startsWith("+")) {
    const digits = collapseNanpCountryCode(trimmed.slice(1).replace(/\D/g, ""));
    const candidate = `+${digits}`;
    return isE164(candidate) ? candidate : undefined;
  }

  // Strip all non-digits for processing
  const digitsOnly = collapseNanpCountryCode(trimmed.replace(/\D/g, ""));
  if (!digitsOnly) return undefined;

  // If looks like a US 10-digit local number and defaultCountryCode is US, prefix +1
  if (digitsOnly.length === 10 && defaultCountryCode === "1") {
    const candidate = `+1${digitsOnly}`;
    return isE164(candidate) ? candidate : undefined;
  }

  // Otherwise assume it already includes a country code
  // Must be between 2 and 15 digits and not start with 0
  if (digitsOnly.length >= 2 && digitsOnly.length <= 15 && digitsOnly[0] !== "0") {
    const candidate = `+${digitsOnly}`;
    return isE164(candidate) ? candidate : undefined;
  }

  return undefined;
}

// Matches a trailing extension (e.g. "x123", "ext. 4", ";#99") so it's dropped before normalizing.
const TRAILING_PHONE_EXTENSION_REGEX = /(?:[;,]?\s*(?:ext(?:ension)?\.?|x|#)\s*:?\s*\d+)\s*$/i;
// Matches a phone-shaped substring: an optional "+", then digits/() with separators, min ~7 digits.
const PHONE_CANDIDATE_REGEX = /\+?[\d(][\d().\-\s]{5,}\d/g;

/**
 * Extracts the first phone-like candidate from free-form input and normalizes it to E.164.
 *
 * Stored member phones can still carry notes, extensions, or multiple saved numbers, so pull the
 * first phone-shaped substring before normalizing. Returns undefined when nothing normalizes to a
 * valid E.164 number.
 *
 * Examples:
 *  - "cell: (415) 555-2671 (leave a voicemail)" -> "+14155552671"
 *  - "4155552671 x204" -> "+14155552671"
 *  - "no phone" -> undefined
 */
export function extractE164PhoneNumber(phone: string | undefined): string | undefined {
  const trimmedPhone = phone?.trim();
  if (!trimmedPhone) return undefined;

  const sanitizedPhone = trimmedPhone
    .replace(/^tel:/i, "")
    .replace(TRAILING_PHONE_EXTENSION_REGEX, "")
    .trim();
  const phoneCandidates = sanitizedPhone.match(PHONE_CANDIDATE_REGEX) ?? [sanitizedPhone];

  return phoneCandidates
    .map((candidate) => toE164PhoneNumber(candidate))
    .find((normalized): normalized is string => Boolean(normalized));
}

/**
 * Strips the US country prefix from a phone number.
 *
 * @param phoneNumber - The phone number to strip.
 * @returns The phone number without the country prefix.
 */
export function stripUSPhonePrefix(phoneNumber: string) {
  if (phoneNumber.length <= 10) return phoneNumber;
  return phoneNumber.replace(/^\+1/, "");
}

/**
 * Formats a phone number for display purposes.
 * Converts "+15551234567" to "(555) 123-4567"
 * Handles US phone numbers and international formats.
 *
 * @param phone - The phone number to format (digits only or with formatting)
 * @returns The formatted phone number or empty string if invalid
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone) return "";

  // Normalize to strict E.164 first for robustness
  const e164 = toE164PhoneNumber(phone);
  if (!e164) return "";

  // If US number (+1...), format as (AAA) PPP-NNNN
  const usMatch = e164.match(/^\+1(\d{10})$/);
  if (usMatch) {
    const usDigits = usMatch[1];
    return `(${usDigits.slice(0, 3)}) ${usDigits.slice(3, 6)}-${usDigits.slice(6)}`;
  }

  // For non-US, return the E.164 string unchanged
  return e164;
}

/**
 * Normalizes a phone number for US phone numbers.
 * Requires exactly 10 digits.
 * Returns the normalized 10-digit number (without +1 prefix), or undefined if invalid.
 *
 * @param phone - The phone number to normalize
 * @returns The 10-digit US phone number, or undefined if not a valid US number
 */
export function normalizeUSPhoneNumber(phone: string | undefined): string | undefined {
  if (!phone) return undefined;

  const e164 = toE164PhoneNumber(phone);
  if (!e164) return undefined;

  // Check if it's a valid US number (+1 followed by 10 digits)
  if (!/^\+1\d{10}$/.test(e164)) {
    return undefined;
  }

  return stripUSPhonePrefix(e164);
}

/**
 * Masks a phone number for log output to protect PII.
 *
 * Keeps only the last 4 digits visible, prefixed with asterisks.
 *
 * @param value - The phone number to mask.
 * @returns The masked phone number, or undefined if the input is falsy.
 */
export function maskPhoneForLogs(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 4) return "****";
  return `***${digits.slice(-4)}`;
}
